import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useId, useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo } from '@/componentes/Campo';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { HistoriaNaMesa } from '@/historias/mesa';
import { formatarDataHora } from '@/lib/formato';
import { comCorpo, explicarFalha, pedir } from '@/lib/pedido';
import { primeiraMaiuscula } from '@/lib/texto';
import { useSessao } from '@/modulos/sessao/consultas';
import { UtilidadeDaResposta } from '@/modulos/tecnico/UtilidadeDaResposta';

/**
 * IA 01 — perguntar sobre a máquina, com citações (decisão 18 do dono: o motor
 * é um serviço de fora, pago por pergunta).
 *
 * A tela mostra SEMPRE de onde veio a resposta: documento, revisão, página e o
 * próprio trecho. Resposta sem trecho que a sustente chega como "não sei", com
 * o que faltou — é o comportamento que o guia exige (seções 6.4 e 11.1), e não
 * uma falha. E cada pergunta tem custo, o que o rótulo da permissão já diz.
 */

interface Citacao {
  readonly trechoId: string;
  readonly titulo: string;
  readonly revisao: string | null;
  readonly pagina: number | null;
  readonly texto: string;
  /** A tela do fato, quando a citação é do histórico da máquina (etapa C). */
  readonly para?: string | null;
}

interface Pergunta {
  readonly id: string;
  readonly pergunta: string;
  readonly sabe: boolean;
  readonly resposta: string;
  readonly citacoes: readonly Citacao[];
  readonly falta: string | null;
  readonly criadoEm: string;
  readonly perguntadoPor?: string | null;
  readonly documentosRestritosDeFora?: number;
  /** Se quem está vendo pediu esta resposta: só ele a avalia (0076). Ausente na resposta recém-pedida, que é sempre de quem pediu. */
  readonly minha?: boolean;
  readonly minhaUtilidade?: boolean | null;
}

export function PerguntaAoManual({ equipamentoId }: { readonly equipamentoId: string }) {
  const base = useId();
  const sessao = useSessao();
  const pode = (sessao.data?.permissoes ?? []).includes('ia.perguntar');
  const cliente = useQueryClient();
  const [texto, setTexto] = useState('');
  const [ultima, setUltima] = useState<Pergunta | null>(null);

  const historico = useQuery({
    queryKey: ['ia', 'perguntas', equipamentoId],
    queryFn: () => pedir<{ perguntas: readonly Pergunta[]; motorConfigurado: boolean }>(`/equipamentos/${equipamentoId}/perguntas`),
    enabled: pode,
  });
  const perguntar = useMutation({
    mutationFn: (pergunta: string) => comCorpo<Pergunta>('POST', `/equipamentos/${equipamentoId}/perguntas`, { pergunta }),
    /* A resposta aparece logo abaixo da pergunta: ela é o retorno. */
    meta: { silencioso: true },
    onSuccess: (p) => {
      setUltima(p);
      setTexto('');
      void cliente.invalidateQueries({ queryKey: ['ia', 'perguntas', equipamentoId] });
    },
  });

  if (!pode) return null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (texto.trim().length < 3) return;
    perguntar.mutate(texto.trim());
  }

  const recusa = perguntar.isError ? explicarFalha(perguntar.error) : null;
  const anteriores = (historico.data?.perguntas ?? []).filter((p) => p.id !== ultima?.id).slice(0, 3);

  return (
    <div className="flex flex-col gap-3 border-t border-borda-sutil pt-3">
      <form onSubmit={enviar} className="flex flex-col gap-2">
        <Campo id={`${base}-pergunta`} rotulo="Perguntar aos documentos desta máquina">
          <input
            id={`${base}-pergunta`}
            className="controle w-full"
            maxLength={1000}
            placeholder="Qual o intervalo de troca do filtro de ar?"
            value={texto}
            onChange={(e) => {
              setTexto(e.target.value);
            }}
          />
        </Campo>
        {/* A frase numa linha propria, com a largura inteira, e o botao
            embaixo, a direita. Lado a lado, o botao de 206 px deixava a frase
            com 93 px: quatro linhas de uma palavra no painel de 360 px. */}
        <div className="flex flex-col gap-2">
          <span className="txt-apoio text-pretty text-tinta-apagada">
            {historico.data?.motorConfigurado === false
              ? 'O assistente ainda não está ligado neste ambiente.'
              : 'A resposta cita documento e página, e o histórico da máquina quando ele explica o caso. Cada pergunta tem custo.'}
          </span>
          <HistoriaNaMesa codigo="BTN-EQP-07" className="flex self-end">
            <Botao
              nivel="consulta"
              type="submit"
              className="self-end"
              disabled={perguntar.isPending || texto.trim().length < 3}
            >
              {/* Verbo + objeto (p.15): "Perguntar" sozinho nao diz a quem. */}
              {perguntar.isPending ? 'Consultando…' : 'Perguntar aos documentos'}
            </Botao>
          </HistoriaNaMesa>
        </div>
      </form>

      {recusa !== null ? (
        <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-3">
          <span className="txt-corpo text-tinta">{primeiraMaiuscula(recusa.erro)}</span>
          <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
        </div>
      ) : null}

      {perguntar.isPending ? (
        /* A IA procura nos documentos (p.32): o lugar da resposta vira a forma
           dela — a pergunta, que já se sabe, o parágrafo e a citação. A
           resposta anterior sai dali: com o botão dizendo "Consultando…", ela
           se lia como a resposta nova. */
        <article className="flex flex-col gap-2 rounded-controle border border-borda-sutil p-3">
          <span className="txt-apoio text-tinta-apagada">{perguntar.variables}</span>
          <Carregando oQue="Procurando nos documentos" className="flex flex-col gap-2">
            <span className="flex flex-col gap-2 py-1">
              <Barra className="h-3 w-full" />
              <Barra className="h-3 w-11/12" />
              <Barra className="h-3 w-3/5" />
            </span>
            <Barra className="my-1 h-3 w-1/2" />
          </Carregando>
        </article>
      ) : ultima ? (
        <Resposta pergunta={ultima} />
      ) : null}
      {anteriores.length > 0 ? (
        <details className="flex flex-col gap-2">
          <summary className="txt-apoio cursor-pointer text-tinta-secundaria">Perguntas anteriores</summary>
          <div className="mt-2 flex flex-col gap-3">
            {anteriores.map((p) => (
              <Resposta key={p.id} pergunta={p} />
            ))}
          </div>
        </details>
      ) : null}
    </div>
  );
}

function Resposta({ pergunta }: { readonly pergunta: Pergunta }) {
  return (
    <article className="flex flex-col gap-2 rounded-controle border border-borda-sutil p-3">
      <span className="txt-apoio text-tinta-apagada">
        {`${pergunta.pergunta} · ${formatarDataHora(new Date(pergunta.criadoEm))}${pergunta.perguntadoPor ? ` · ${pergunta.perguntadoPor}` : ''}`}
      </span>
      <p className="txt-corpo text-tinta">{pergunta.resposta}</p>
      {!pergunta.sabe && pergunta.falta ? (
        <p className="txt-apoio text-tinta-secundaria">{`O que falta: ${pergunta.falta}.`}</p>
      ) : null}
      {pergunta.citacoes.length > 0 ? (
        <HistoriaNaMesa codigo="NUM-BIB-04">
          <ol className="flex list-decimal flex-col gap-1 pl-5">
            {pergunta.citacoes.map((c) => (
              <li key={c.trechoId} className="txt-apoio text-tinta-secundaria">
                <span className="text-tinta">
                  {[c.titulo, c.revisao ? `revisão ${c.revisao}` : null, c.pagina !== null ? `página ${String(c.pagina)}` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                {` — “${c.texto.length > 220 ? `${c.texto.slice(0, 220)}…` : c.texto}”`}
                {/* O link interno depois do fato, como no resumo do painel: o fato é a prova e fica legível inteiro. */}
                {c.para ? (
                  <>
                    {' '}
                    <Link to={c.para} className="text-tinta-secundaria underline">
                      Abrir a tela
                    </Link>
                  </>
                ) : null}
              </li>
            ))}
          </ol>
        </HistoriaNaMesa>
      ) : null}
      {pergunta.documentosRestritosDeFora ? (
        <p className="txt-apoio text-tinta-apagada">
          {pergunta.documentosRestritosDeFora === 1
            ? '1 documento restrito sobre o assunto ficou de fora: documento restrito não é lido pelo serviço de fora.'
            : `${String(pergunta.documentosRestritosDeFora)} documentos restritos sobre o assunto ficaram de fora: documento restrito não é lido pelo serviço de fora.`}
        </p>
      ) : null}
      {pergunta.minha !== false ? <UtilidadeDaResposta perguntaId={pergunta.id} inicial={pergunta.minhaUtilidade ?? null} /> : null}
    </article>
  );
}
